/* global DYO */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useClientSideABTest } from '../abtest/components/abtest';

import { DynamicYield } from '../core/src/dynamic-yield-client';
import { useSlugInfo } from './usePage';
import { Helpers } from '../core/src/helpers';
import { useTestTrackingData } from '../abtest/useTestFlags';

const EMPTY_OBJECT = {};

const getDeviceTrackingData = () => {
    try {
        return {
            context: {
                device: {
                    userAgent: navigator.userAgent,
                },
            },
            options: {
                isImplicitClientData: true,
            },
        };
    } catch (e) {}
};

const deriveCategoryFromBreadcrumbs = seo => {
    const { breadcrumbs } = seo || {};
    return breadcrumbs.reduce((r, b, idx) => {
        if (idx === 0 && b.label.toLowerCase() === 'home') {
            return r;
        }
        r.push(b.id);
        return r;
    }, []);
};

export const getContext = slugInfo => {
    let type = 'OTHER';
    let data;
    try {
        const { components, templateKey, seo } = slugInfo || EMPTY_OBJECT;
        const { productInitial } = components || EMPTY_OBJECT;
        const { sku } = productInitial || EMPTY_OBJECT;
        if (templateKey === 'home') {
            type = 'HOMEPAGE';
        } else if (['cat', 'mlp', 'mdp', 'vwa'].includes(templateKey)) {
            type = 'CATEGORY';
            const categories = deriveCategoryFromBreadcrumbs(seo);
            data = categories && categories.length > 0 ? categories : undefined;
        } else if (!!sku) {
            type = 'PRODUCT';
            data = [sku];
        } else if (templateKey === 'cart') {
            type = 'CART';
        }
    } catch (e) {}
    return { type, data };
};

const getAPIClient = Helpers.memoize(config => {
    return new DynamicYield(config);
});

const selectConfig = Helpers.memoize(
    (id, searchEndPoints) => {
        return (searchEndPoints || {})[id];
    },
    (id, searchEndPoints) => `${id}_${Object.keys(searchEndPoints).join('_')}`,
);

export const transformDYProductRecs = resp => {
    try {
        const { slots, wId, fId, fetching, ...rest } = resp || {};
        const dyContainerTracking = { 'data-dy-widget-id': wId, 'data-dy-feed-id': fId };
        const productRecs = slots.map(s => {
            const { item, strId } = s;
            const { sku } = item;
            return {
                ...item,
                catentryId: item.catid,
                images: [{ image: item.image_url }],
                productUrl: item.url,
                dyItemTracking: { 'data-dy-product-id': sku, 'data-dy-strategy-id': strId },
                ctaViewDetailsLink: item.url,
            };
        });

        return { productRecs, dyContainerTracking, fetching, ...rest };
    } catch (e) {
        return { productRecs: [], dyContainerTracking: {}, fetching: true };
    }
};

const getDY = () => {
    try {
        return window.DYO;
    } catch (e) {
        return undefined;
    }
};

const useDynamicYieldRecs = (groupId, componentKey, options, transform, widgetRef) => {
    const [recs, setRecs] = useState({});
    const stateKey = `${groupId}_${componentKey}`;
    const { current: widgetElement } = widgetRef || {};

    const DYO = getDY();

    useEffect(() => {
        if (widgetElement && widgetElement.dataset.dyWidgetId) {
            try {
                DYO.recommendations.registerElements(widgetElement);
            } catch (e) {}
        }
    }, [widgetElement]);
    useEffect(() => {
        //if no groupId is passed do nothing
        //if using a stateKey that already has data do nothing
        if (!DYO || !groupId || stateKey in recs) return;

        try {
            setRecs({ ...recs, [stateKey]: { fetching: true } });
            DYO.recommendationWidgetData(groupId, options, (error, data) => {
                if (error) {
                    setRecs({ ...recs, [stateKey]: { error, fetching: false } });
                    return;
                }

                setRecs({ ...recs, [stateKey]: { ...data, fetching: false } });
            });
        } catch (e) {
            setRecs({ ...recs, [stateKey]: { fetching: true } });
        }
        //if you add options as a dependancy, it will cause an infinite loop unless you memoize the options object
    }, [stateKey, DYO]);

    return transform ? transformDYProductRecs(recs[stateKey]) : recs[stateKey];
};
export default useDynamicYieldRecs;

/**
 * Returns sorted products first the first n products in the list based on the size
 * the remaining products are returned in the order they are passed.
 * @param {*} groupId - Strategy ID
 * @param {*} componentKey - Unique key for the component
 * @param {*} products - List of products to be sorted
 * @param {*} size - Number of products to sort
 * @returns
 */
export const useDynamicYieldSort = (groupId, componentKey, products, size = 10) => {
    try {
        //if groupId is prefixed with 'ab:' then use the abTest hook to get the groupId
        const abTestId = typeof groupId === 'string' ? groupId.split('ab:')[1] : null;
        const { groupId: abTestGroupId } = useClientSideABTest(abTestId) || {};
        const resolvedGroupId = abTestId ? abTestGroupId : groupId;
        //pass null groupId to avoid calling DY if no products are passed
        const { slots } =
            useDynamicYieldRecs(!products || !products.length ? null : resolvedGroupId, componentKey, {
                realtimeRules: [
                    {
                        id: -1,
                        query: {
                            conditions: [
                                {
                                    field: 'group_id',
                                    arguments: products.slice(0, size).map(s => ({
                                        action: 'IS',
                                        value: s.sku,
                                    })),
                                },
                            ],
                        },
                        type: 'include',
                        slots: [],
                    },
                ],
            }) || {};

        if (Array.isArray(slots)) {
            //returns products in the order they are returned from DY
            const sortedList = products
                .slice()
                .sort(
                    (a, b) => slots.findIndex(p => p.item.sku === a.sku) - slots.findIndex(p => p.item.sku === b.sku),
                );
            return { products: sortedList, loading: false };
        }

        return { products, loading: true };
    } catch (e) {
        return { products, loading: false };
    }
};

export const useDYAPI = () => {
    const dyAPIConfig = useSelector(state => selectConfig('dyclient', state.searchEndPoints));
    return dyAPIConfig ? getAPIClient(dyAPIConfig) : null;
};

export const useDYChoose = (names, pageData) => {
    const [choices, setChoices] = useState({});
    const [dyAPIAvailable, setDYAPIAvailable] = useState(false);
    const [hasFetchedAfterConsent, setHasFetchedAfterConsent] = useState(false);
    const dyClient = useDYAPI();
    const slugInfo = useSlugInfo();
    const {
        trackingEnabled,
        hasDeterminedTracking
    } = useTestTrackingData();

    useEffect(() => {
        try {
            //filter names not already in choices
            const toFetch = names.filter(name => !choices[name]);
            const { templateKey, vanityUrl } = slugInfo || {};
            const { type } = getContext(slugInfo) || {};
            if (
                (hasDeterminedTracking || hasFetchedAfterConsent) &&
                toFetch &&
                toFetch.length &&
                dyAPIAvailable &&
                dyClient &&
                templateKey &&
                vanityUrl
            ) {
                const trackingOptions = trackingEnabled ? getDeviceTrackingData() : {};
                const { options, context } = trackingOptions || {};
                setChoices({
                    ...choices,
                    ...toFetch.reduce((acc, name) => ({ ...acc, [name]: { fetching: true } }), {}),
                });
                setHasFetchedAfterConsent(true);
                dyClient
                    .choose(toFetch, pageData, context, { type, ...(options || {}) })
                    .then(data => {
                        setChoices({ ...choices, ...data });
                    })
                    .catch(error => {
                        setChoices({
                            ...choices,
                            ...toFetch.reduce((acc, name) => ({ ...acc, [name]: { error } }), {}),
                        });
                    });
            }
        } catch (e) {}
    }, [names, dyAPIAvailable, dyClient, slugInfo, hasDeterminedTracking]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        let attempts = 0;
        const maxAttempts = 50;

        const interval = setInterval(() => {
            try {
                attempts++;
                if (window.DYO !== undefined && window.DY?.dyid) {
                    setDYAPIAvailable(true);
                    clearInterval(interval);
                } else if (attempts >= maxAttempts) {
                    clearInterval(interval);
                    console.warn('window.DYO not available after max attempts');
                }
            } catch (e) {}
        }, 300);

        return () => clearInterval(interval);
    }, []);

    return names.reduce((acc, name, i) => {
        acc[name] = choices[name];
        return acc;
    }, {});
};
